import { supabase } from "@/integrations/supabase/client";
import {
  buildFunctionInvokeError,
  FunctionInvokeError,
  type FunctionInvokeErrorDetails,
} from "./functionInvokeErrors";

/**
 * Backend error codes that indicate the caller's session is at AAL2 but the
 * underlying TOTP assertion is older than the freshness window required for
 * the requested action. The frontend must re-prompt for TOTP and retry.
 */
const STEP_UP_REQUIRED_ERROR_CODES = new Set([
  "mfa_recent_required",
  "mfa_assertion_too_old",
]);

/**
 * Backend error code for an `aal=aal2` session that lacks any TOTP `amr`
 * entry, or for a session that is still at `aal=aal1`. Treated the same as
 * a stale assertion for retry purposes — the user has to verify TOTP.
 */
const FULL_MFA_REQUIRED_ERROR_CODES = new Set(["mfa_required"]);

/**
 * Canonical code returned when an SSO session hits an AAL2 step-up gate it does
 * not satisfy (ENT-3463 P3, spec §L192).
 * @deprecated ENT-4204 — Phase 2 replaces this uniform denial with
 * `SSO_REASSERTION_REQUIRED_CODE` (a re-authenticate-with-your-IdP CTA) on the
 * customer-facing gates. The default-deny STAFF gates still emit it:
 * `admin-api-keys`, `breakglass-request`, and `scan-worker-rollout-preflight`
 * (ENT-4515 removed `admin-elevate-role`, which now accepts a fresh in-app TOTP).
 * Do NOT remove this export while any gate defaults to `'deny'`.
 */
export const SSO_STEP_UP_UNAVAILABLE_CODE = "sso_step_up_unavailable";

/**
 * Ratified Phase-1 copy for the uniform SSO step-up denial.
 * @deprecated ENT-4204 — the default-deny staff gates (`admin-api-keys`,
 * `breakglass-request`, `scan-worker-rollout-preflight`) still surface this;
 * customer-facing surfaces use the re-assertion CTA (ENT-4515 removed
 * `admin-elevate-role` from the deny set). Centralised here so no render path
 * hardcodes the string. Do NOT remove while any gate defaults to `'deny'`.
 */
export const SSO_STEP_UP_UNAVAILABLE_MESSAGE =
  "This action requires re-authentication — available for SSO accounts in Phase 2";

/**
 * Canonical code returned when an SSO session's IdP assertion is too old to
 * satisfy a step-up gate (ENT-4204 Phase 2). Unlike a TOTP step-up it is NOT
 * resolved by a code prompt — the UI re-runs SSO login (`signInWithSSO`) so the
 * IdP mints a fresh assertion, then returns the user to the originating route to
 * retry. `detectSsoReassertionRequired` extracts it.
 */
export const SSO_REASSERTION_REQUIRED_CODE = "sso_reassertion_required";

export interface StepUpRequiredError {
  /** Server-supplied canonical errorCode. */
  code: string;
  functionName?: string;
  /** Human-readable message safe to surface. */
  message?: string;
}

/**
 * Pull the canonical error body out of a `supabase.functions.invoke` error.
 * FunctionsHttpError wraps the response in `.context` (a Response in newer SDKs,
 * a plain object in older ones); callers may also pass an already-parsed body.
 */
async function extractCanonicalErrorBody(
  error: unknown,
): Promise<{
  errorCode?: string;
  code?: string;
  error?: string;
  functionName?: string;
} | null> {
  if (!error || typeof error !== "object") return null;

  const ctx = (error as { context?: unknown }).context;
  let body: {
    errorCode?: string;
    code?: string;
    error?: string;
    functionName?: string;
  } | null = null;

  if (ctx && typeof ctx === "object") {
    if (typeof (ctx as Response).json === "function") {
      try {
        body = await (ctx as Response).clone().json();
      } catch {
        body = null;
      }
    } else if (typeof ctx === "object") {
      body = ctx as {
        errorCode?: string;
        code?: string;
        error?: string;
        functionName?: string;
      };
    }
  }

  if (!body) {
    body = error as {
      errorCode?: string;
      code?: string;
      error?: string;
      functionName?: string;
    };
  }
  return body;
}

/**
 * Inspect a `supabase.functions.invoke` error response and decide whether
 * it indicates a step-up requirement. Returns the parsed canonical error
 * shape if so; otherwise `null`.
 *
 * The Supabase JS client wraps non-2xx responses into a FunctionsHttpError
 * whose `.context.body` contains the function's JSON. We pull errorCode out
 * of that body — falling through to plain object inspection for callers
 * that pass already-parsed responses.
 */
export async function detectStepUpRequired(
  error: unknown,
): Promise<StepUpRequiredError | null> {
  const body = await extractCanonicalErrorBody(error);
  if (!body) return null;

  const candidate = body.errorCode ?? body.code;
  if (!candidate) return null;
  // `sso_step_up_unavailable` is intentionally excluded: an SSO session has no
  // TOTP factor to challenge, so it must NOT trigger the retry prompt.
  if (
    STEP_UP_REQUIRED_ERROR_CODES.has(candidate) ||
    FULL_MFA_REQUIRED_ERROR_CODES.has(candidate)
  ) {
    return {
      code: candidate,
      message: body.error,
      functionName: body.functionName,
    };
  }
  return null;
}

/**
 * Detect the `sso_step_up_unavailable` denial (ENT-3463 P3). Returns the parsed
 * error (with the server's ratified message) when an SSO session was refused a
 * step-up-gated action, else null. A UI can use this to surface the message
 * ("available for SSO accounts in Phase 2") rather than a raw error, without
 * ever prompting for a TOTP the SSO user cannot provide.
 */
export async function detectSsoStepUpUnavailable(
  error: unknown,
): Promise<StepUpRequiredError | null> {
  const body = await extractCanonicalErrorBody(error);
  if (!body) return null;
  const candidate = body.errorCode ?? body.code;
  if (candidate === SSO_STEP_UP_UNAVAILABLE_CODE) {
    return {
      code: candidate,
      message: body.error,
      functionName: body.functionName,
    };
  }
  return null;
}

/**
 * Detect the `sso_reassertion_required` denial (ENT-4204 Phase 2). Returns the
 * parsed error when an SSO session's IdP assertion was too old for a step-up
 * gate, else null. A UI uses this to drive the re-authentication CTA
 * (`signInWithSSO`) rather than a TOTP prompt the SSO user cannot answer.
 */
export async function detectSsoReassertionRequired(
  error: unknown,
): Promise<StepUpRequiredError | null> {
  const body = await extractCanonicalErrorBody(error);
  if (!body) return null;
  const candidate = body.errorCode ?? body.code;
  if (candidate === SSO_REASSERTION_REQUIRED_CODE) {
    return {
      code: candidate,
      message: body.error,
      functionName: body.functionName,
    };
  }
  return null;
}

/**
 * Resolve the user's verified TOTP factor id, if any. Returns null when
 * the user has no enrolled factor — in that case step-up cannot proceed
 * and the caller must surface a "please enrol MFA first" message.
 */
async function resolveVerifiedTotpFactorId(): Promise<string | null> {
  const { data, error } = await supabase.auth.mfa.listFactors();
  if (error) return null;
  const factor = data?.totp?.find((f) => f.status === "verified");
  return factor?.id ?? null;
}

export interface StepUpPromptInput {
  /** User-facing reason shown above the TOTP entry (e.g. "Block this user"). */
  reason: string;
  /**
   * Which step-up the prompt must drive (ENT-4204). `'totp'` (default) collects
   * a six-digit code; `'sso'` renders the re-authenticate-with-your-IdP CTA and
   * performs the `signInWithSSO` redirect itself (no code input).
   */
  mode?: "totp" | "sso";
}

export interface StepUpPromptResult {
  /** 6-digit TOTP code entered by the user. */
  code: string;
}

/**
 * Caller-supplied prompt function. The retry wrapper invokes this when the
 * backend returns `mfa_recent_required` so the UI can drive the TOTP
 * dialog. Returning `null` cancels the retry.
 */
export type StepUpPrompt = (
  input: StepUpPromptInput,
) => Promise<StepUpPromptResult | null>;

export interface InvokeWithStepUpOptions<TArgs> {
  /** Reason surfaced in the step-up prompt if the backend rejects with stale AAL2. */
  stepUpReason: string;
  /** Caller-supplied prompt. Resolves to the entered code or null to cancel. */
  prompt: StepUpPrompt;
  /** Original invocation arguments to retry verbatim after refreshing AAL2. */
  args: TArgs;
}

export class StepUpCancelledError extends Error {
  constructor() {
    super("Step-up cancelled by user");
    this.name = "StepUpCancelledError";
  }
}

export class StepUpFactorMissingError extends Error {
  constructor() {
    super("No verified TOTP factor available for step-up");
    this.name = "StepUpFactorMissingError";
  }
}

/**
 * Thrown-shaped error carrying the backend's ratified `sso_step_up_unavailable`
 * message (ENT-3463 P3). `invokeWithStepUp` returns this in place of the raw
 * `FunctionsHttpError` so every caller surfaces the Phase-1 guidance rather than
 * the generic "Edge Function returned a non-2xx status code" transport message.
 * @deprecated ENT-4204 — Phase 2 drives an SSO re-assertion CTA on customer
 * gates; the default-deny staff gates (`admin-api-keys`, `breakglass-request`,
 * `scan-worker-rollout-preflight`) still produce this (ENT-4515 removed
 * `admin-elevate-role`). Do NOT remove while any gate defaults to `'deny'`.
 */
export class SsoStepUpUnavailableError extends FunctionInvokeError {
  readonly code = SSO_STEP_UP_UNAVAILABLE_CODE;
  constructor(message?: string, details: FunctionInvokeErrorDetails = {}) {
    super(
      message && message.trim().length > 0
        ? message
        : "This action requires re-authentication — available for SSO accounts in Phase 2",
      { ...details, errorCode: SSO_STEP_UP_UNAVAILABLE_CODE },
    );
    this.name = "SsoStepUpUnavailableError";
  }
}

async function normalizeSsoStepUpUnavailable(
  error: unknown,
): Promise<SsoStepUpUnavailableError | null> {
  const ssoDenied = await detectSsoStepUpUnavailable(error);
  if (!ssoDenied) return null;

  const normalized = await buildFunctionInvokeError(
    "step-up-gated-operation",
    error,
    "This action requires re-authentication — available for SSO accounts in Phase 2",
  );
  return new SsoStepUpUnavailableError(
    ssoDenied.message ?? normalized.message,
    {
      functionName: normalized.functionName,
      status: normalized.status,
      responseBody: normalized.responseBody,
      responseJson: normalized.responseJson,
      errorClass: normalized.errorClass,
      rpcName: normalized.rpcName,
      requestId: normalized.requestId,
      isAbort: normalized.isAbort,
      isFetchError: normalized.isFetchError,
    },
  );
}

/**
 * Wrap a function invocation with automatic step-up retry. If the first
 * call returns `mfa_recent_required` / `mfa_assertion_too_old` /
 * `mfa_required`, prompts the user for a fresh TOTP, calls
 * `mfa.challengeAndVerify`, then retries the invocation once.
 *
 * The invocation function receives the original `args` unchanged. Supabase
 * keeps the access token in the auth session, so a successful
 * challengeAndVerify automatically updates the JWT used by subsequent
 * `supabase.functions.invoke` calls — no token-passing required.
 */
export async function invokeWithStepUp<TArgs, TResult>(
  invocation: (
    args: TArgs,
  ) => Promise<{ data: TResult | null; error: unknown }>,
  options: InvokeWithStepUpOptions<TArgs>,
): Promise<{ data: TResult | null; error: unknown }> {
  const first = await invocation(options.args);
  const stepUp = await detectStepUpRequired(first.error);
  if (!stepUp) {
    // ENT-4204 Phase 2: an SSO session whose IdP assertion went stale. Drive the
    // re-authentication CTA — the SSO prompt performs the `signInWithSSO`
    // redirect itself (page navigates away and returns via `stepup_resume`), so
    // there is no TOTP verify/retry here. A null result means the user cancelled.
    const ssoReassert = await detectSsoReassertionRequired(first.error);
    if (ssoReassert) {
      const prompted = await options.prompt({
        reason: options.stepUpReason,
        mode: "sso",
      });
      if (!prompted) {
        throw new StepUpCancelledError();
      }
      // Unreachable in practice: the SSO dialog navigates away on continue. If it
      // somehow resolves without navigating, surface the original error unchanged.
      return first;
    }
    // Legacy uniform SSO denial (deprecated, `ssoPolicy: 'deny'` surfaces only).
    // Normalise the raw FunctionsHttpError into a clean error carrying the
    // server's ratified message (no TOTP prompt — the SSO user has no factor to
    // challenge). Codex #2783 R4 P2.
    const normalized = await normalizeSsoStepUpUnavailable(first.error);
    if (normalized) {
      return {
        data: null,
        error: normalized,
      };
    }
    return first;
  }

  const factorId = await resolveVerifiedTotpFactorId();
  if (!factorId) {
    throw new StepUpFactorMissingError();
  }

  const prompted = await options.prompt({ reason: options.stepUpReason });
  if (!prompted) {
    throw new StepUpCancelledError();
  }

  const { error: verifyError } = await supabase.auth.mfa.challengeAndVerify({
    factorId,
    code: prompted.code,
  });
  if (verifyError) {
    return { data: null, error: verifyError };
  }

  // Token updated in-session — retry the original invocation once. If it
  // still rejects, do not loop; normalize an SSO denial so a cross-tab session
  // change cannot leak a raw FunctionsHttpError into the UI.
  const retried = await invocation(options.args);
  const retrySsoDenial = await normalizeSsoStepUpUnavailable(retried.error);
  return retrySsoDenial ? { data: null, error: retrySsoDenial } : retried;
}
